import axios from 'axios';
import { CommentStatusChip } from '@/pages/fulfillment/components/CommentStatus';
import { CheckCell } from '@/pages/fulfillment/components/CheckCell';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Container, Dropdown, Form, InputGroup, Modal, Spinner, Table } from 'react-bootstrap';
import { kstDay, kstTimestamp } from '@/utils/fulfillmentTime';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { AUTO_DISMISS, useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import type {
  FulfillmentLinePurchaseState,
  FulfillmentOrderLineSummary,
  FulfillmentOrderRow,
  ListOrdersParams,
} from '@/infrastructure/FulfillmentAdminClient';
import { marketingBadgeStyle, marketingRowStyle } from '@/utils/fulfillmentMarketing';
import { downloadTrackingExportCSV } from '@/pages/oms/orders/utils/parxlCsvExport';
import { writeYunExpressExcel } from '@/pages/oms/orders/utils/yunExpressExport';
import { filterReadyYunOrders, toExportOrders } from '@/utils/fulfillmentExportOrders';
import { lineRouteLabel, purchasePlatformLabel } from '@/utils/fulfillmentSource';
import { AddOrdersModal } from '@/pages/fulfillment/orders/components/AddOrdersModal';
import { CancelLockBadge } from '@/pages/fulfillment/components/CancelLockBadge';
import { CancelSampleAssignmentModal } from '@/pages/fulfillment/orders/components/CancelSampleAssignmentModal';
import { ImportTrackingModal } from '@/pages/fulfillment/orders/components/ImportTrackingModal';
import { SampleAssignmentOnModal } from '@/pages/fulfillment/orders/components/SampleAssignmentOnModal';
import { SampleSetModal } from '@/pages/fulfillment/orders/components/SampleSetModal';
import { StockHistoryLink } from '@/pages/fulfillment/components/StockHistoryLink';
import { CommentIcon, ExternalIcon, PlusIcon, SearchIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import { OrdersTablePaging } from '@/pages/oms/orders/components/OrdersTablePaging';
import type { StackedCell } from '@/utils/fulfillmentOrderColumns';
import {
  describeCustomer,
  describeShippingAddress,
  describeTotalValue,
  EMPTY_CELL,
} from '@/utils/fulfillmentOrderColumns';
import {
  carrierBadgeStyle,
  carrierLabelOf,
  FULFILLMENT_ORDER_COLUMNS,
  fulfillmentTableMinWidth,
  listedShipmentOf,
  statusTintStyle,
} from '@/utils/fulfillmentOrderTable';
import { fulfillmentStatusLabel } from '@/utils/fulfillmentOrderStatus';
import { CsStatusBadge } from '@/components/common/CsStatusBadge';
import { getAdminApiUrl, AdminApiDomain } from '@/config/ApiRouting';
import { ORDERS_ADD_ORDERS_PARAM, ROUTES } from '@/routes/routes';
import '../ultimateDesign.css';
import './ordersList.css';
import {
  buildFulfillmentOrdersUrlParams,
  ORDER_SOURCES,
  COUNTRY_CODES,
  PAGE_SIZES,
  readFulfillmentOrdersUrlState,
  FULFILLMENT_STATUSES,
  CS_STATUS_FILTERS,
  INBOUND_TRACKING_FILTERS,
  PURCHASE_ORDER_FILTERS,
} from './urlState';
import type { PurchasePresenceFilter } from './urlState';

/** The API's page cap; the Yun sheet reads the whole filtered set, so it walks every page at the widest size. */
const EXPORT_PAGE_SIZE = 100;
/** A runaway guard, not a business limit: 200 pages is 20,000 orders, far past any one date range. */
const EXPORT_MAX_PAGES = 200;

/** Every row the filter matches, page by page, because the carrier sheet must not stop at the page on screen. */
const listEveryOrder = async (
  client: FulfillmentAdminClient,
  filters: Omit<ListOrdersParams, 'limit' | 'offset'>,
): Promise<FulfillmentOrderRow[]> => {
  const rows: FulfillmentOrderRow[] = [];
  for (let pageIndex = 0; pageIndex < EXPORT_MAX_PAGES; pageIndex++) {
    const params: ListOrdersParams = { ...filters, limit: EXPORT_PAGE_SIZE };
    if (rows.length > 0) {
      params.offset = rows.length;
    }
    const res = await client.listOrders(params);
    rows.push(...res.orders);
    const reachedTotal = typeof res.totalCount === 'number' && rows.length >= res.totalCount;
    if (res.orders.length < EXPORT_PAGE_SIZE || reachedTotal) {
      return rows;
    }
  }
  throw new Error('The filtered list is too large to export; narrow the date range.');
};

const YUN_EMPTY_WARNING = 'No READY YUN_EXPRESS orders match the current filter.';

/**
 * Whether the server reads each line's purchase state, which is also what lets it filter on the two
 * purchase axes (SSD-7396). An older server refuses those params, so the screen asks only once a
 * response has shown the field.
 */
const carriesPurchaseState = (orders: FulfillmentOrderRow[]): boolean =>
  orders.some((order) => (order.lines ?? []).some((line) => line.purchaseState !== undefined));

/** The body a refused admin write answers with; ValidationPipe sends an array, a guard sends one line. */
interface AdminActionErrorBody {
  message?: string | string[];
}

/** Both shapes have to read as one sentence: a validator answers with an array, a guard with a line. */
const refusalOf = (err: unknown): string | undefined => {
  const message = axios.isAxiosError<AdminActionErrorBody>(err) ? err.response?.data?.message : undefined;
  const text = Array.isArray(message) ? message.join(' · ') : message;
  return text || undefined;
};

const mergeRefusalOf = (err: unknown): string => refusalOf(err) ?? 'Could not merge these orders. Nothing was changed.';

/** Reloading cannot fix a query the server refused, so only an unexplained failure is worth retrying. */
const listFailureOf = (err: unknown): string =>
  refusalOf(err) ?? 'Could not load orders. Reload the page to try again.';

/** Derives display band from order_id range: WC<1e9, marketing 1e9..2e9, internal>=2e9 */
const deriveBand = (orderId: number): string => {
  if (orderId >= 2_000_000_000) {
    return 'internal';
  }
  if (orderId >= 1_000_000_000) {
    return 'marketing';
  }
  return 'wc';
};

/**
 * One cell for where the order came from and who issued its number. The band is derived from the id
 * range, so a WC-channel order can still be internally issued (a clone keeps the source channel).
 */
/** A multi-line order stacks one entry per line, matching how the OMS renders its product columns. */
const renderLineCell = (order: FulfillmentOrderRow, read: (line: FulfillmentOrderLineSummary) => string | null) => {
  const texts = (order.lines ?? []).map(read).filter((text): text is string => text !== null && text.length > 0);
  if (texts.length === 0) {
    return EMPTY_CELL;
  }
  return texts.map((text, index) => <div key={`${order.orderId}-${index}`}>{text}</div>);
};

/**
 * V1's words for a live JIT line still waiting on its purchase, so a many-line order says which line
 * holds it up. A line with its waybill already shows the number, so it needs no word.
 */
const PURCHASE_STATE_WORDS: Partial<Record<FulfillmentLinePurchaseState, string>> = {
  NOT_PURCHASED: 'NA',
  PURCHASED_NO_TRACKING: 'Preparing',
};

/** The SKU column is three facts, so the sku leads and the sourcing trail follows in muted type. */
const renderSkuCell = (order: FulfillmentOrderRow) => {
  const lines = order.lines ?? [];
  if (lines.length === 0) {
    return EMPTY_CELL;
  }
  return lines.map((line, index) => {
    const trail = [
      line.sourcingRoute ? lineRouteLabel(line.sourcingRoute, purchasePlatformLabel(line.purchasePlatform)) : null,
      line.sourcingOrderNumber,
      line.sourcingTrackingNumber,
    ]
      .filter(Boolean)
      .join(' / ');
    const stateWord = line.purchaseState ? PURCHASE_STATE_WORDS[line.purchaseState] : undefined;
    return (
      <div key={`${order.orderId}-sku-${index}`} className={index > 0 ? 'mt-1' : undefined}>
        {/* The sku opens its own Stock History in a new tab, as it does on View Orders (SSD-7520). */}
        <div className="font-monospace">
          <StockHistoryLink sku={line.sku} />
        </div>
        {(trail || stateWord) && (
          <div className="text-muted small">
            {trail}
            {stateWord && (
              <span className="badge text-bg-warning ms-1" data-testid="line-purchase-state">
                {stateWord}
              </span>
            )}
          </div>
        )}
      </div>
    );
  });
};

const describeSource = (order: FulfillmentOrderRow): string => {
  const band = deriveBand(order.orderId);
  const qualifiers: string[] = [];
  if (band !== 'wc') {
    qualifiers.push(band);
  }
  if (order.isMarketing && band !== 'marketing') {
    qualifiers.push('marketing');
  }
  return [order.channel, ...qualifiers].join(' · ');
};

/** Carrier references are read character by character, so they stay monospaced and clip rather than wrap. */
const renderReference = (value: string | null | undefined) => (
  // A reference made of spaces is no reference: it shows the usual dash, not an empty cell (SSD-7781).
  <span className="font-monospace text-truncate d-block">{value?.trim() || EMPTY_CELL}</span>
);

/** The carrier as a coloured badge, or the usual dash while no carrier holds the parcel. */
const renderProviderCell = (provider: string | null) =>
  provider ? (
    <span className="badge" style={carrierBadgeStyle(provider)}>
      {carrierLabelOf(provider)}
    </span>
  ) : (
    EMPTY_CELL
  );

/** What a carrier field holds before a waybill exists; none of these is a number to search. */
const NOT_A_WAYBILL = new Set(['', '-', EMPTY_CELL]);

/**
 * The order's waybill opens the order in View Orders by its address (SSD-7520), as Order Detail's waybill does
 * (SSD-7296): searching the number instead would read an outbounded order's own waybill as a customer return.
 * The link sits inside the clipping span, so a long number still ends in an ellipsis on one line.
 */
const renderTrackingCell = (orderId: number, value: string | null | undefined) => {
  const waybill = value?.trim() ?? '';
  if (NOT_A_WAYBILL.has(waybill)) {
    return renderReference(value);
  }
  return (
    <span className="font-monospace text-truncate d-block">
      <Link
        to={ROUTES.fulfillment.viewOrdersOrder(orderId)}
        className="font-monospace"
        target="_blank"
        rel="noopener noreferrer"
        title={`Open order ${orderId} in View Orders (new tab)`}
      >
        {waybill}
      </Link>
    </span>
  );
};

/** The scannable value on top, its qualifier underneath in muted type. */
const renderStacked = (cell: StackedCell) => (
  <>
    <div className="text-truncate">{cell.primary}</div>
    {cell.secondary && <div className="text-muted small text-truncate">{cell.secondary}</div>}
  </>
);

/** One place at a time: a hit is only readable if the operator knows which column answered. */
type SearchScope = 'order' | 'tracking' | 'comments' | 'pic';

type SearchScopeEntry = { value: SearchScope; label: string; ariaLabel: string; placeholder: string };

const ORDER_SCOPE: SearchScopeEntry = {
  value: 'order',
  label: 'Order #',
  ariaLabel: 'Order #',
  placeholder: 'Order no.',
};

const SEARCH_SCOPES: SearchScopeEntry[] = [
  ORDER_SCOPE,
  { value: 'tracking', label: 'Tracking #', ariaLabel: 'Tracking #', placeholder: 'Waybill no.' },
  { value: 'comments', label: 'Comments', ariaLabel: 'Comments scope', placeholder: 'Words in a comment' },
  { value: 'pic', label: 'PIC', ariaLabel: 'PIC scope', placeholder: 'PIC name or email' },
];

/**
 * The box always looks here; the choice above adds to it rather than replacing it. PIC is the exception: "whose
 * orders are these" is one column, so that scope narrows by the PIC alone (SSD-7327).
 */
const ALWAYS_SEARCHED = 'Always, except PIC: customer email & phone, SKU, product name, purchase order #';

/** A label shown at the width of its widest form, so swapping it never moves what sits beside it. */
const SteadyLabel = ({ label, reserve }: { label: string; reserve: string[] }) => (
  <span className="orders-steady-label">
    {reserve.map((text) => (
      <span key={text} aria-hidden="true">
        {text}
      </span>
    ))}
    <span>{label}</span>
  </span>
);

/** Every label the scope toggle can show; the toggle reserves the widest. */
const SCOPE_TOGGLE_LABELS = SEARCH_SCOPES.map((entry) => `Search in: ${entry.label}`);

/** The PARXL button counts at most one page of rows. */
const PARXL_WIDEST_LABEL = `Carrier export (PARXL) (${Math.max(...PAGE_SIZES)})`;

const YUN_EXPORT_LABEL = 'Carrier export (YUN)';
const YUN_EXPORTING_LABEL = 'Exporting Yun…';

/** Manual carrier cancels read for the line above the list; the line counts them, the page lists them. */
const MANUAL_CANCELS_LIMIT = 100;

/** The open manual carrier cancels as the line above the list counts them; 'failed' when they could not be read. */
type ManualCancelsCount = { count: number; more: boolean } | 'failed';

export default function Fulfillment2Page() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);

  const [searchParams, setSearchParams] = useSearchParams();
  // Read once: after this the filter state owns the URL, so a later write cannot loop back as a fresh seed.
  const initialFiltersRef = useRef(readFulfillmentOrdersUrlState(searchParams));
  const initialFilters = initialFiltersRef.current;

  const [source, setSource] = useState(initialFilters.source);
  const [fulfillmentStatus, setFulfillmentStatus] = useState(initialFilters.fulfillmentStatus);
  const [csStatus, setCsStatus] = useState(initialFilters.csStatus);
  const [startDate, setStartDate] = useState(initialFilters.startDate);
  const [endDate, setEndDate] = useState(initialFilters.endDate);
  const [countryCode, setCountryCode] = useState(initialFilters.countryCode);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchasePresenceFilter>(initialFilters.purchaseOrder);
  const [inboundTracking, setInboundTracking] = useState<PurchasePresenceFilter>(initialFilters.inboundTracking);
  // Learned from the responses and kept once seen: a filtered page with no rows says nothing about the server.
  const [purchaseFiltersSupported, setPurchaseFiltersSupported] = useState(false);
  // Manual hold is independent of CS status and warehouse progress.
  const [onHoldOnly, setOnHoldOnly] = useState(false);
  // The OMS PIC box searches on submit, so typing does not fire a request per keystroke.
  const [pageSize, setPageSize] = useState<number>(initialFilters.pageSize);
  const [searchDraft, setSearchDraft] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  // Order number is the search an operator reaches for most, so it starts on; waybills are opt-in.
  const [searchScope, setSearchScope] = useState<SearchScope>('order');
  // Picking a scope closes the menu and puts the cursor in the box: the next thing the operator does is type.
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  const searchBoxRef = useRef<HTMLInputElement>(null);
  // Roster names, offered as suggestions while the PIC scope is on; an order can still carry a name the roster dropped.
  const [picNames, setPicNames] = useState<string[]>([]);
  // The comment ledger is a different store, so it is a scope on this box rather than a second box [G-7].
  const [showMerge, setShowMerge] = useState<boolean>(false);
  const [showImport, setShowImport] = useState<boolean>(false);
  const [showSampleOn, setShowSampleOn] = useState<boolean>(false);
  const [showSampleCancel, setShowSampleCancel] = useState<boolean>(false);
  const [showSampleSet, setShowSampleSet] = useState<boolean>(false);
  // Opened by the button, or by a link to the retired Marketing & Samples screen; the URL write below drops the flag.
  const [showAddOrders, setShowAddOrders] = useState<boolean>(() => searchParams.has(ORDERS_ADD_ORDERS_PARAM));
  const [mergePrimary, setMergePrimary] = useState<number | null>(null);
  const [merging, setMerging] = useState<boolean>(false);
  const [mergeRefusal, setMergeRefusal] = useState<string>('');
  // A merge rewrites rows the list is showing, so it has to refetch even though no filter changed.
  const [refreshedAt, setRefreshedAt] = useState<number>(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  /** Null until the first read answers. */
  const [manualCancels, setManualCancels] = useState<ManualCancelsCount | null>(null);
  const [orders, setOrders] = useState<FulfillmentOrderRow[]>([]);
  // 1-based, like the OMS pager the operator already counts in.
  const [page, setPage] = useState<number>(1);
  // Null, not zero: a backend without the count has nothing to report, which is not the same as no orders.
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  /** The list read failing is a state the screen stays in; every write reports through the toast [T03]. */
  const [error, setError] = useState<string>('');
  const { toasts, showFailure, showWarning, dismiss } = useFulfillmentToast();
  // 15 columns is more than anyone reads at once; hidden keys stay out of the layout entirely.
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  /** The rows themselves, not their ids: intersecting with the page on screen ran bulk actions on a smaller set. */
  const [selected, setSelected] = useState<Map<number, FulfillmentOrderRow>>(new Map());

  const selectedIds = useMemo(() => [...selected.keys()], [selected]);
  // BR-18 client-side: the operator sees why before the server refuses, and the button stays honest.
  const mixesMarketing = useMemo(() => {
    const marketing = selectedIds.map((orderId) => orderId >= 1_000_000_000 && orderId < 2_000_000_000);
    return marketing.some(Boolean) && !marketing.every(Boolean);
  }, [selectedIds]);

  const pageCount = totalCount === null ? page : Math.max(Math.ceil(totalCount / pageSize), 1);
  // Without a total, a full page is the only hint that another one exists.
  const hasNextPage = totalCount === null ? orders.length === pageSize : page < pageCount;

  const exportOrders = useMemo(() => toExportOrders(orders), [orders]);
  const [exporting, setExporting] = useState(false);

  /**
   * The whole filtered set, from the server, with the columns the table shows [D-s]. The page on screen
   * is a window over the filter; exporting the window would answer a question nobody asked.
   */
  const exportTable = async () => {
    setExporting(true);
    try {
      const { limit: _limit, offset: _offset, ...filters } = buildParams();
      const csv = await client.exportOrdersCsv(filters);
      if (typeof URL.createObjectURL !== 'function') {
        return;
      }
      const href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = `fulfillment-orders-${kstDay(new Date().toISOString())}.csv`;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoking in the same tick can cancel the download the click just started.
      setTimeout(() => URL.revokeObjectURL(href), 0);
    } catch (failure) {
      showFailure(failure, 'The order list could not be exported.');
    } finally {
      setExporting(false);
    }
  };
  const [exportingYun, setExportingYun] = useState(false);
  // The OMS modal asked for a date range; here the filter row supplies it and names the file.
  const canExportYun = Boolean(startDate && endDate);

  /** The OMS Yun sheet over the whole filtered set: 2.0 supplies the rows, the template stays in the OMS module. */
  const exportYun = async () => {
    const { limit: _limit, offset: _offset, ...filters } = buildParams();
    // Only READY orders go on the sheet, so a status filter naming anything else has nothing to export.
    if (filters.fulfillmentStatus && filters.fulfillmentStatus !== 'READY') {
      showWarning(YUN_EMPTY_WARNING, undefined, AUTO_DISMISS);
      return;
    }
    setExportingYun(true);
    try {
      // Asking the server for READY alone keeps the walk to the orders that can land on the sheet.
      const ready = await listEveryOrder(client, { ...filters, fulfillmentStatus: 'READY' });
      // A held order must not ship, and OMS never listed one (on-hold is not prepare-shipment), even under "On hold only".
      const rows = filterReadyYunOrders(toExportOrders(ready.filter((row) => !row.holdReason)));
      if (rows.length === 0) {
        showWarning(YUN_EMPTY_WARNING, undefined, AUTO_DISMISS);
        return;
      }
      writeYunExpressExcel(rows, startDate, endDate);
    } catch (failure) {
      showFailure(failure, 'The Yun Express sheet could not be exported.');
    } finally {
      setExportingYun(false);
    }
  };

  const visibleColumns = useMemo(
    () => FULFILLMENT_ORDER_COLUMNS.filter((column) => !hiddenColumns.has(column.key)),
    [hiddenColumns],
  );

  const toggleColumn = useCallback((key: string) => {
    setHiddenColumns((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const allSelected = orders.length > 0 && orders.every((order) => selected.has(order.orderId));

  /** The header tick works this page only: clearing it must not silently drop selections made elsewhere. */
  const toggleAll = useCallback(
    (checked: boolean) =>
      setSelected((current) => {
        const next = new Map(current);
        orders.forEach((order) => (checked ? next.set(order.orderId, order) : next.delete(order.orderId)));
        return next;
      }),
    [orders],
  );

  const toggleOne = useCallback((order: FulfillmentOrderRow) => {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(order.orderId)) {
        next.delete(order.orderId);
      } else {
        next.set(order.orderId, order);
      }
      return next;
    });
  }, []);

  /** One map per row so the header and the body cannot drift apart when a column is hidden. */
  const renderOrderCells = useCallback(
    (order: FulfillmentOrderRow): Record<string, ReactNode> => ({
      orderId: (
        // The id still opens Order Detail; the icon beside it opens the order in View Orders by its address,
        // so nothing is searched (SSD-7520). Both, and the comment count, stay on one line inside the column's fixed 184px.
        <>
          <span className="order-id-links d-inline-flex align-items-center gap-1 text-nowrap">
            <Link to={ROUTES.fulfillment.orderDetail(order.orderId)} className="font-monospace text-nowrap">
              {order.orderId}
            </Link>
            <Link
              to={ROUTES.fulfillment.viewOrdersOrder(order.orderId)}
              className="order-view-orders-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open order ${order.orderId} in View Orders`}
              title="Open in View Orders (new tab)"
            >
              <ExternalIcon />
            </Link>
            {/* The notes the desk left on the order, seen without opening it (FC request 13, SSD-7567). */}
            {order.commentCount ? (
              <Link
                to={ROUTES.fulfillment.orderDetail(order.orderId)}
                className="order-comment-count"
                aria-label={`${order.commentCount} ${order.commentCount === 1 ? 'comment' : 'comments'} on order ${order.orderId}`}
                title={`${order.commentCount} ${order.commentCount === 1 ? 'comment' : 'comments'} · open the order to read`}
              >
                <CommentIcon />
                {/* Three digits would push the chip past the column; the exact count is in the label and title. */}
                {order.commentCount > 99 ? '99+' : order.commentCount}
              </Link>
            ) : null}
          </span>
          {/* Where the order's issue stands by its thread, under the id so the fixed column keeps its width (SSD-7609). */}
          {order.commentStatus && (
            <div className="order-comment-status">
              <CommentStatusChip status={order.commentStatus} title="The status of the latest comment that set one" />
            </div>
          )}
        </>
      ),
      orderDate: <span className="text-nowrap">{kstTimestamp(order.orderPlacedAt)}</span>,
      source: (
        <>
          <span className="fw-semibold">{describeSource(order)}</span>
          {order.isMarketing && (
            <span className="badge ms-2" style={marketingBadgeStyle}>
              MKT
            </span>
          )}
          {/* The order team's own number for the parcel, which the search box also finds (SSD-7457). */}
          {order.externalOrderNo && (
            <div className="small text-muted font-monospace" title="External order number">
              {order.externalOrderNo}
            </div>
          )}
        </>
      ),
      country: order.countryCode ?? EMPTY_CELL,
      status: (
        <>
          {/* Manual hold remains visible without hiding the warehouse progress. */}
          <span
            className="badge rounded-pill"
            style={statusTintStyle(order.holdReason != null ? 'HELD' : order.fulfillmentStatus)}
            title={order.holdReason != null ? `Manual hold: ${order.holdReason}` : 'Warehouse fulfillment status'}
          >
            {order.holdReason != null
              ? 'ON HOLD'
              : order.fulfillmentStatus
                ? fulfillmentStatusLabel(order.fulfillmentStatus, order.resolutionKind)
                : EMPTY_CELL}
          </span>
          {order.holdReason != null && (
            <>
              <div className="small text-muted">Manual hold</div>
              <div>
                {order.fulfillmentStatus
                  ? fulfillmentStatusLabel(order.fulfillmentStatus, order.resolutionKind)
                  : EMPTY_CELL}
              </div>
            </>
          )}
          {/* Under the chip, inside the column's fixed width: the column never grows for it (SSD-7450). */}
          <CancelLockBadge lock={order} variant="row" />
        </>
      ),
      csStatus: order.csStatus ? <CsStatusBadge status={order.csStatus} /> : '',
      provider: renderProviderCell(listedShipmentOf(order.shipment).provider),
      providerOrderId: renderReference(order.shipment?.providerOrderId),
      pic: order.picUser ?? EMPTY_CELL,
      customer: renderStacked(describeCustomer(order)),
      shippingAddress: renderStacked(describeShippingAddress(order)),
      totalValue: <span className="text-nowrap">{describeTotalValue(order)}</span>,
      totalItems: order.totalQuantity,
      productNameKr: renderLineCell(order, (line) => line.productNameKr),
      sku: renderSkuCell(order),
      trackingNo: renderTrackingCell(order.orderId, listedShipmentOf(order.shipment).trackingNumber),
    }),
    [],
  );

  /** An empty box is not a filter — sending it would ask the server for orders whose PIC is the empty string. */
  const buildParams = useCallback((): ListOrdersParams => {
    const params: ListOrdersParams = { limit: pageSize };
    if (searchScope === 'pic') {
      // A filter, not a search: it narrows with the JIT and status filters (AND) and never matches buyer or product text.
      if (search.trim()) {
        params.pic = search.trim();
      }
    } else if (search) {
      params.search = search;
      params.searchOrderNo = searchScope === 'order';
      params.searchTracking = searchScope === 'tracking';
      params.searchComments = searchScope === 'comments';
      params.searchPic = false;
    }
    const picked = ORDER_SOURCES.find((entry) => entry.value === source);
    if (picked?.band) {
      params.band = picked.band;
    }
    if (picked?.channel) {
      params.channel = picked.channel;
    }
    if (fulfillmentStatus.trim()) {
      params.fulfillmentStatus = fulfillmentStatus.trim();
    }
    if (csStatus) {
      params.csStatus = csStatus;
    }
    if (startDate) {
      params.startDate = startDate;
    }
    if (endDate) {
      params.endDate = endDate;
    }
    if (countryCode.trim()) {
      params.countryCode = countryCode.trim().toUpperCase();
    }
    if (onHoldOnly) {
      params.onHoldOnly = true;
    }
    if (purchaseFiltersSupported && purchaseOrder) {
      params.purchaseOrder = purchaseOrder;
    }
    if (purchaseFiltersSupported && inboundTracking) {
      params.inboundTracking = inboundTracking;
    }
    if (page > 1) {
      params.offset = (page - 1) * pageSize;
    }
    return params;
  }, [
    source,
    fulfillmentStatus,
    csStatus,
    startDate,
    endDate,
    countryCode,
    onHoldOnly,
    purchaseOrder,
    inboundTracking,
    purchaseFiltersSupported,
    pageSize,
    page,
    search,
    searchScope,
  ]);

  // Page 4 of the old filter set means nothing, so narrowing the list starts over at the first page.
  const filterSignature = [
    source,
    fulfillmentStatus,
    csStatus,
    startDate,
    endDate,
    countryCode,
    onHoldOnly,
    purchaseOrder,
    inboundTracking,
    pageSize,
    search,
    searchScope,
  ].join('|');
  useEffect(() => {
    setPage(1);
  }, [filterSignature]);

  // The box takes one kind of term at a time, so it asks for that one rather than listing the others.
  const scope = SEARCH_SCOPES.find((entry) => entry.value === searchScope) ?? ORDER_SCOPE;

  /**
   * Every CreatorHub line is a JIT line nobody buys, so "Order #: absent" across all sources is mostly
   * marketing orders. Narrowing the purchase axes from All sources starts at WooCommerce instead.
   */
  const pickPurchaseFilter = (set: (value: PurchasePresenceFilter) => void, value: PurchasePresenceFilter) => {
    set(value);
    if (value && source === 'all') {
      setSource('wc');
    }
  };

  const submitSearch = useCallback(() => {
    setSearch(searchDraft);
  }, [searchDraft]);

  const lastWrittenSearchRef = useRef<string | null>(null);

  /** The filter row lives in the URL, so a refresh, a bookmark and a pasted link all reopen the same list. */
  useEffect(() => {
    const next = buildFulfillmentOrdersUrlParams({
      source,
      fulfillmentStatus,
      csStatus,
      startDate,
      endDate,
      countryCode,
      pageSize,
      purchaseOrder,
      inboundTracking,
    }).toString();
    if (lastWrittenSearchRef.current === next) {
      return;
    }
    lastWrittenSearchRef.current = next;
    // Replace, not push: a filter change is a refinement of this view, not a page the Back button owes the operator.
    setSearchParams(next, { replace: true });
  }, [
    source,
    fulfillmentStatus,
    csStatus,
    startDate,
    endDate,
    countryCode,
    pageSize,
    purchaseOrder,
    inboundTracking,
    setSearchParams,
  ]);

  useEffect(() => {
    let ignore = false;
    client
      .getPicRoster()
      .then((roster) => {
        if (!ignore) {
          setPicNames([...new Set(roster.pics.map((profile) => profile.name.trim()).filter(Boolean))].sort());
        }
      })
      .catch(() => {
        // Without suggestions the PIC scope still takes whatever is typed.
      });
    return () => {
      ignore = true;
    };
  }, [client]);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError('');
    client
      .listOrders(buildParams())
      .then((res) => {
        if (!ignore) {
          setOrders(res.orders);
          setTotalCount(res.totalCount ?? null);
          setLastRefreshedAt(new Date());
          if (carriesPurchaseState(res.orders)) {
            setPurchaseFiltersSupported(true);
          }
        }
      })
      .catch((err) => {
        if (!ignore) {
          console.error('Error fetching fulfillment orders:', err);
          setError(listFailureOf(err));
          // Rows from the previous query would read as the answer to this one.
          setOrders([]);
          setTotalCount(null);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [buildParams, client, refreshedAt]);

  /**
   * Waybills a person has to cancel at the carrier (SSD-7586) are one line above the list rather than a menu item
   * (SSD-7596), read when the page opens and whenever the list itself is read again.
   */
  useEffect(() => {
    let ignore = false;
    client
      .listManualCarrierCancels('OPEN', MANUAL_CANCELS_LIMIT)
      .then((list) => {
        if (!ignore) {
          // Only the server knows whether the list goes on past what it sent.
          setManualCancels({ count: list.rows.length, more: list.truncated === true });
        }
      })
      .catch(() => {
        if (!ignore) {
          setManualCancels('failed');
        }
      });
    return () => {
      ignore = true;
    };
  }, [client, refreshedAt]);

  // Background reads must not disturb an open workflow or overlap a list request.
  const refreshPaused = loading || showMerge || showImport || showSampleOn || showSampleCancel ||
    showSampleSet || showAddOrders || merging || exporting || exportingYun;
  useEffect(() => {
    const refresh = () => {
      if (!refreshPaused && document.visibilityState === 'visible') {
        setRefreshedAt((n) => n + 1);
      }
    };
    const interval = window.setInterval(refresh, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [refreshPaused]);

  return (
    <Container fluid className="ff-ultimate-page wms-orders">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <div className="d-flex align-items-baseline gap-3 flex-wrap">
            <h2 className="mb-1">Order Management Dashboard</h2>
            {totalCount !== null && <span className="text-muted">{totalCount.toLocaleString('en-US')} orders</span>}
          </div>
          <div className="text-muted small">
            Auto-refresh every 5 minutes · Last refreshed:{' '}
            {lastRefreshedAt ? (
              <time dateTime={lastRefreshedAt.toISOString()}>{kstTimestamp(lastRefreshedAt.toISOString())}</time>
            ) : (
              'Not yet'
            )}
          </div>
          {startDate && endDate && (
            <div className="text-muted small">
              {startDate} to {endDate}
            </div>
          )}
        </div>
        {/* Marketing and gifting orders are added where they land, not on a screen of their own (SSD-7448). */}
        <Button variant="primary" onClick={() => setShowAddOrders(true)}>
          <PlusIcon />
          Add orders
        </Button>
      </div>

      {error && (
        <Alert variant="danger" role="alert">
          {error}
        </Alert>
      )}
      <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />
      {/* Nothing when none are open; a failed read is said quietly, since the list itself is unaffected. */}
      {manualCancels === 'failed' ? (
        <div className="text-muted small mb-2" data-testid="orders-manual-cancels">
          Could not check waybills to cancel by hand ·{' '}
          <Link to={ROUTES.fulfillment.manualCarrierCancels}>Open Manual carrier cancels</Link>
        </div>
      ) : manualCancels && manualCancels.count > 0 ? (
        <Alert variant="warning" className="py-2 mb-2" data-testid="orders-manual-cancels">
          <Link to={ROUTES.fulfillment.manualCarrierCancels}>
            {manualCancels.count}
            {manualCancels.more ? '+' : ''} {manualCancels.count === 1 && !manualCancels.more ? 'waybill' : 'waybills'}{' '}
            to cancel at the carrier by hand
          </Link>
        </Alert>
      ) : null}

      {/*
        Two rows, because the controls answer two different questions: the first finds an order and
        acts on what came back, the second narrows the ledger. Sixteen controls in one wrap row left
        no reading order at all. Both stay placeholder-driven — the uppercase micro-labels outweighed
        the controls they named.
      */}
      <div data-testid="orders-narrow-row" className="d-flex flex-wrap gap-2 align-items-center mb-2">
        {/* One question: the id lane and the channel column only ever cut into these five. */}
        <Form.Select
          size="sm"
          className="w-auto"
          aria-label="Source"
          value={source}
          onChange={(e) => setSource(e.target.value as typeof source)}
        >
          {ORDER_SOURCES.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          size="sm"
          className="w-auto"
          aria-label="Fulfillment status"
          value={fulfillmentStatus}
          onChange={(e) => setFulfillmentStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {FULFILLMENT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {fulfillmentStatusLabel(status)}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          size="sm"
          className="w-auto"
          aria-label="CS status"
          title="CS status, independent of warehouse progress, WooCommerce and manual hold"
          value={csStatus}
          onChange={(e) => setCsStatus(e.target.value)}
        >
          <option value="">All CS statuses</option>
          {CS_STATUS_FILTERS.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </Form.Select>
        {/* One range, so the two ends read as a pair rather than as two unrelated date boxes. */}
        <InputGroup size="sm" className="w-auto">
          <Form.Control
            className="w-auto"
            type="date"
            aria-label="Start date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <InputGroup.Text>to</InputGroup.Text>
          <Form.Control
            className="w-auto"
            type="date"
            aria-label="End date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </InputGroup>
        {/* The catalogue's own country list, so a typo cannot filter the desk down to nothing. */}
        {/* This is only the operator's manual hold, not WC on-hold. */}
        <Form.Check
          type="checkbox"
          className="align-self-center"
          id="orders-on-hold-only"
          label="Manual hold only"
          checked={onHoldOnly}
          onChange={(e) => setOnHoldOnly(e.target.checked)}
        />
        <Form.Select
          size="sm"
          className="w-auto"
          style={{ maxWidth: 140 }}
          aria-label="Country"
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
        >
          <option value="">All countries</option>
          {COUNTRY_CODES.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </Form.Select>
        {/* Two independent axes over live JIT lines; together they ask the same line, so V1's Preparing is both. */}
        {purchaseFiltersSupported && (
          <>
            <Form.Select
              size="sm"
              className="w-auto"
              aria-label="Purchase order number"
              title="Live JIT lines (ordered or awaiting inbound) with or without a purchase order number"
              value={purchaseOrder}
              onChange={(e) => pickPurchaseFilter(setPurchaseOrder, e.target.value as PurchasePresenceFilter)}
            >
              {PURCHASE_ORDER_FILTERS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              size="sm"
              className="w-auto"
              aria-label="Inbound tracking"
              title="Live JIT lines with or without an inbound waybill; Order # present + Tracking absent is V1's Preparing"
              value={inboundTracking}
              onChange={(e) => pickPurchaseFilter(setInboundTracking, e.target.value as PurchasePresenceFilter)}
            >
              {INBOUND_TRACKING_FILTERS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </Form.Select>
          </>
        )}
      </div>

      <div data-testid="orders-find-row" className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          {/* Reading the note does not close the menu; picking a scope does, and focuses the box. */}
          <Dropdown autoClose="outside" show={scopeMenuOpen} onToggle={(next) => setScopeMenuOpen(next)}>
            <Dropdown.Toggle size="sm" variant="outline-secondary" id="search-scope" className="orders-scope-toggle">
              <SteadyLabel label={`Search in: ${scope.label}`} reserve={SCOPE_TOGGLE_LABELS} />
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-3" style={{ minWidth: 260 }}>
              {/* Naming what is always searched stops the choice reading as the whole of it. */}
              <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                {ALWAYS_SEARCHED}
              </div>
              {SEARCH_SCOPES.map((scope) => (
                <Form.Check
                  key={scope.value}
                  type="radio"
                  name="search-scope"
                  id={`search-scope-${scope.value}`}
                  label={scope.label}
                  aria-label={scope.ariaLabel}
                  checked={searchScope === scope.value}
                  onChange={() => setSearchScope(scope.value)}
                  // On click, not change: picking the scope already in use fires no change but still closes the menu.
                  onClick={() => {
                    setScopeMenuOpen(false);
                    searchBoxRef.current?.focus();
                  }}
                />
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <InputGroup size="sm" style={{ width: 240 }}>
            <Form.Control
              ref={searchBoxRef}
              aria-label="Search"
              placeholder={scope.placeholder}
              list={searchScope === 'pic' ? 'orders-pic-names' : undefined}
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  submitSearch();
                }
              }}
            />
            <Button variant="dark" aria-label="Search orders" onClick={submitSearch}>
              <SearchIcon />
              Search
            </Button>
          </InputGroup>
          <datalist id="orders-pic-names">
            {picNames.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </div>
        {/* Everything that acts on the result set, held to the right so the search box keeps the eye. */}
        <div className="orders-actions ms-auto d-flex flex-wrap align-items-center">
          <Form.Select
            size="sm"
            className="w-auto"
            aria-label="Rows per page"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} rows
              </option>
            ))}
          </Form.Select>
          {/* Each export answers its own question, so each says which one it answers [D-s]. */}
          {/* Reuses the OMS carrier modules: 2.0 supplies the rows, the CSV/XLSX shapes stay in one place. */}
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={exportOrders.length === 0}
            onClick={() => downloadTrackingExportCSV(exportOrders, startDate, endDate)}
          >
            <SteadyLabel label={`Carrier export (PARXL) (${exportOrders.length})`} reserve={[PARXL_WIDEST_LABEL]} />
          </Button>
          <Button size="sm" variant="outline-secondary" disabled={exporting} onClick={() => void exportTable()}>
            Export table (CSV)
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={exportingYun || !canExportYun}
            title={
              canExportYun
                ? 'READY YUN_EXPRESS orders in the filtered list, as the Yun Express standard template.'
                : 'Pick a start and end date to export the Yun Express sheet.'
            }
            onClick={() => void exportYun()}
          >
            <SteadyLabel
              label={exportingYun ? YUN_EXPORTING_LABEL : YUN_EXPORT_LABEL}
              reserve={[YUN_EXPORT_LABEL, YUN_EXPORTING_LABEL]}
            />
          </Button>
          <Dropdown>
            <Dropdown.Toggle size="sm" variant="outline-secondary" id="sample-assignment">
              Sample assignment
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item as="button" onClick={() => setShowSampleOn(true)}>
                Turn sample assignment on
              </Dropdown.Item>
              <Dropdown.Item as="button" onClick={() => setShowSampleCancel(true)}>
                Cancel sample assignment
              </Dropdown.Item>
              <Dropdown.Divider />
              {/* What every open window gives away, kept beside the windows it feeds. */}
              <Dropdown.Item as="button" onClick={() => setShowSampleSet(true)}>
                Edit sample set
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle size="sm" variant="outline-secondary">
              Tracking
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item as="button" onClick={() => setShowImport(true)}>
                Import tracking numbers
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown show={showColumnPicker} onToggle={setShowColumnPicker} autoClose="outside">
            <Dropdown.Toggle size="sm" variant="outline-secondary">
              Columns
            </Dropdown.Toggle>
            <Dropdown.Menu className="px-3 py-2">
              {FULFILLMENT_ORDER_COLUMNS.map((column) => (
                <Form.Check
                  key={column.key}
                  type="checkbox"
                  id={`column-toggle-${column.key}`}
                  label={column.label}
                  aria-label={column.label}
                  checked={!hiddenColumns.has(column.key)}
                  onChange={() => toggleColumn(column.key)}
                />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/*
        Everything that acts on the ticked rows, in one row under the filters: what is selected, and
        the three things an operator does with a selection.
      */}
      {selectedIds.length > 0 && (
        <div data-testid="orders-selection-row" className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <span className="orders-selected-count">{selectedIds.length} selected</span>
          <Button
            size="sm"
            variant="primary"
            disabled={selectedIds.length < 2}
            onClick={() => {
              setMergePrimary(selectedIds[0] ?? null);
              setMergeRefusal('');
              setShowMerge(true);
            }}
          >
            Merge Orders ({selectedIds.length})
          </Button>
          <Button size="sm" variant="link" className="p-0" onClick={() => setSelected(new Map())}>
            Clear selection
          </Button>
        </div>
      )}

      <ImportTrackingModal
        show={showImport}
        client={client}
        onHide={() => setShowImport(false)}
        onImported={() => setRefreshedAt((n) => n + 1)}
      />

      <SampleAssignmentOnModal
        show={showSampleOn}
        client={client}
        selectedOrderIds={selectedIds}
        onHide={() => setShowSampleOn(false)}
        onStarted={() => setRefreshedAt((n) => n + 1)}
      />

      <CancelSampleAssignmentModal
        show={showSampleCancel}
        client={client}
        onHide={() => setShowSampleCancel(false)}
        onCancelled={() => setRefreshedAt((n) => n + 1)}
      />

      <SampleSetModal show={showSampleSet} client={client} onHide={() => setShowSampleSet(false)} />

      <AddOrdersModal
        show={showAddOrders}
        client={client}
        onHide={() => setShowAddOrders(false)}
        onImported={() => setRefreshedAt((n) => n + 1)}
      />

      <Modal show={showMerge} onHide={() => setShowMerge(false)} {...ULTIMATE_MODAL}>
        <Modal.Header closeButton>
          <Modal.Title>Merge Orders</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mergeRefusal && (
            <Alert variant="danger" className="py-2">
              {mergeRefusal}
            </Alert>
          )}
          {mixesMarketing ? (
            <p className="text-danger mb-0">Marketing orders cannot be merged with sales orders.</p>
          ) : (
            <>
              <p>Select the order to keep. The others are merged into it and then cancelled.</p>
              {selectedIds.map((orderId) => (
                <Form.Check
                  key={orderId}
                  type="radio"
                  name="mergePrimary"
                  id={`merge-primary-${orderId}`}
                  label={`Keep order ${orderId}`}
                  aria-label={`Keep order ${orderId}`}
                  checked={mergePrimary === orderId}
                  onChange={() => setMergePrimary(orderId)}
                />
              ))}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowMerge(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            disabled={mixesMarketing || mergePrimary == null || merging}
            onClick={async () => {
              if (mergePrimary == null) {
                return;
              }
              setMerging(true);
              setMergeRefusal('');
              try {
                await client.mergeOrders({
                  targetOrderId: mergePrimary,
                  sourceOrderIds: selectedIds.filter((id) => id !== mergePrimary),
                });
                setShowMerge(false);
                setSelected(new Map());
                setRefreshedAt((n) => n + 1);
              } catch (err) {
                // The server names the order and the reason; closing here would take both away.
                setMergeRefusal(mergeRefusalOf(err));
              } finally {
                setMerging(false);
              }
            }}
          >
            Merge
          </Button>
        </Modal.Footer>
      </Modal>

      <Card className="mb-3">
        <Card.Header className="fw-semibold">Showing {orders.length.toLocaleString('en-US')}</Card.Header>
        <Card.Body className="p-0">
          {/* The table keeps its declared widths and scrolls; squeezing 15 columns starved the CJK name column. */}
          <div className="table-responsive">
            <Table
              hover
              className="mb-0 align-middle"
              style={{ tableLayout: 'fixed', minWidth: fulfillmentTableMinWidth(visibleColumns) }}
            >
              {/* The dark head of the design paints these cells; the grey small-type classes would fight it. */}
              <thead>
                {/* Column order mirrors the live OMS list so an operator's eye lands where it always has. */}
                <tr>
                  <CheckCell as="th" style={{ width: 48 }}>
                    <Form.Check
                      type="checkbox"
                      aria-label="Select all orders"
                      checked={allSelected}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </CheckCell>
                  {visibleColumns.map((column) => (
                    <th
                      key={column.key}
                      style={{ width: column.width }}
                      className={column.alignEnd ? 'text-end' : undefined}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const cells = renderOrderCells(order);
                  return (
                    <tr key={order.orderId} style={order.isMarketing ? marketingRowStyle : undefined}>
                      <CheckCell style={{ overflow: 'hidden' }}>
                        <Form.Check
                          type="checkbox"
                          aria-label={`Select order ${order.orderId}`}
                          checked={selected.has(order.orderId)}
                          onChange={() => toggleOne(order)}
                        />
                      </CheckCell>
                      {visibleColumns.map((column) => (
                        // Fixed layout does not clip on its own; without this an overlong value spills
                        // across the next cell instead of ending in an ellipsis.
                        <td
                          key={column.key}
                          className={`cell-${column.key}${column.alignEnd ? ' text-end' : ''}`}
                          style={{ overflow: 'hidden' }}
                        >
                          {cells[column.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {orders.length === 0 && !loading && !error && (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="text-center text-muted py-4">
                      No orders match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {loading && (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" size="sm" role="status" aria-label="Loading orders" />
            </div>
          )}
        </Card.Body>
      </Card>

      <OrdersTablePaging
        pageNumber={page}
        pageCount={pageCount}
        hasPrevPage={page > 1}
        hasNextPage={hasNextPage}
        onPrevPage={() => setPage((current) => Math.max(current - 1, 1))}
        onNextPage={() => setPage((current) => current + 1)}
      />
    </Container>
  );
}
